// context-binding.ts — komponierbare Bindung eines Elements an EINEN Context-Wert
// (aus dem Context-Store, per Key). Ersetzt die Basisklasse ContextScopedElement
// (PFS-666): statt zu ERBEN HÄLT das Element eine Bindung — Komposition/Adapter
// ([[feedback_composition_over_inheritance]]: „Abstraktion als Objekt, das man hält").
//
// Auflösungs-Priorität: injizierte `valueSource` > `scopeAttr`-Attribut > Host via
// `requestSource`. Re-resolve bei `PFS_CONTEXT_CHANGED_EVENT` UND bei Änderung des
// scopeAttr (per MutationObserver — kein observedAttributes/attributeChangedCallback
// nötig, konsistent mit dem Rest der Library). `onResolve(value)` läuft bei connect
// (initial) und bei jeder ECHTEN Wert-Änderung (gleicher Wert → kein Re-Paint).
import { requestSource, PFS_CONTEXT_CHANGED_EVENT } from "../store/context-store.js";
import type { ValueSource } from "../store/context-store.js";

export interface ContextBindingOptions {
  /** Context-Key (Source-Name), an den gebunden wird. */
  key: string;
  /** data-Attribut für den EXPLIZITEN Wert (konstante Source, z.B. Parent→Child). Default "data-scope". */
  scopeAttr?: string;
  /** Läuft bei connect + jeder echten Wert-Änderung mit dem aufgelösten Wert. */
  onResolve: (value: string | null) => void;
}

/** Host, der optional eine Quelle direkt injiziert bekommt (Vorrang vor Attribut/Host). */
interface SourcedHost extends HTMLElement {
  valueSource?: ValueSource;
}

export class ContextBinding {
  private readonly host: SourcedHost;
  private readonly key: string;
  private readonly scopeAttr: string;
  private readonly onResolve: (value: string | null) => void;
  private connected = false;
  private painted = false;
  private resolved: string | null = null;
  private hostSource: ValueSource | null = null;
  private attrObs: MutationObserver | null = null;
  private readonly onContextChanged = (): void => {
    if (this.connected) this.resolve();
  };

  constructor(host: HTMLElement, opts: ContextBindingOptions) {
    this.host = host as SourcedHost;
    this.key = opts.key;
    this.scopeAttr = opts.scopeAttr ?? "data-scope";
    this.onResolve = opts.onResolve;
  }

  /** Der aufgelöste Context-Wert (oder null). */
  get value(): string | null {
    return this.resolved;
  }

  /** In connectedCallback aufrufen — startet Auflösung + Subscriptions. Idempotent:
   *  ein zweiter connect() ohne dazwischenliegendes disconnect() ist ein No-op (kein
   *  Doppel-Subscribe / kein verwaister MutationObserver). */
  connect(): void {
    if (this.connected) return;
    this.connected = true;
    document.body.addEventListener(PFS_CONTEXT_CHANGED_EVENT, this.onContextChanged);
    this.attrObs = new MutationObserver(() => {
      if (this.connected) this.resolve();
    });
    this.attrObs.observe(this.host, { attributes: true, attributeFilter: [this.scopeAttr] });
    this.resolve(); // synchrone Source → value vor dem ersten onResolve verfügbar
  }

  /** In disconnectedCallback aufrufen. */
  disconnect(): void {
    this.connected = false;
    this.painted = false;
    // ?. — bei Teardown (document.body geleert/null) sonst TypeError (wie die alte Basis).
    document.body?.removeEventListener(PFS_CONTEXT_CHANGED_EVENT, this.onContextChanged);
    this.attrObs?.disconnect();
    this.attrObs = null;
  }

  // Priorität: direkt injizierte valueSource > scopeAttr-Attribut > vom Host via
  // requestSource. Das Element liest NIE direkt URL/Store; reflektiert nur den
  // aufgelösten Wert (data-scope-resolved) fürs Debugging.
  private effectiveSource(): ValueSource | null {
    if (this.host.valueSource) return this.host.valueSource;
    const explicit = this.host.getAttribute(this.scopeAttr);
    if (explicit) return () => explicit;
    return (this.hostSource ??= requestSource(this.host, this.key));
  }

  private resolve(): void {
    const source = this.effectiveSource();
    if (!source) {
      this.apply(null);
      return;
    }
    const v = source();
    if (v instanceof Promise) void v.then((val) => { if (this.connected) this.apply(val ?? null); });
    else this.apply(v ?? null);
  }

  private apply(value: string | null): void {
    const r = value ?? "";
    if (this.host.dataset.scopeResolved !== r) this.host.dataset.scopeResolved = r;
    const changed = value !== this.resolved;
    this.resolved = value;
    if (changed || !this.painted) {
      this.painted = true;
      this.onResolve(value);
    }
  }
}
